import axios from 'axios';
import { omit } from 'lodash';

import { Form, Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form as FormB, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import {
  fetchPaises,
  fetchProvincias,
  generosSelector,
  paisesSelector,
  pciasSelector,
  sexosSelector,
  userSelector,
} from '../../appStore/commonSlice';
import { CVAPI, USERAPI } from '../../const';
import es from '../../yup-es';
import { AlertaDialog } from '../CompUtils/Alerta';
import FormSection from '../CompUtils/FormSection';
import LinkBtn from '../CompUtils/LinkBtn';
import LoadingText from '../CompUtils/LoadingText';
import { finalize, init } from '../GlobalFeedback/feedbackSlice';
yup.setLocale(es);

export interface UsuarioProps {
  apellido: string;
  calle: string;
  ciudad: { id: number; idProvincia: number; nombre: string };
  codPostal: string;
  cuil: string;
  departamento: string;
  dni: string;
  email: string;
  emailAlternativo: string;
  fechaNacimiento: string;
  genero: string;
  idUsuario: number;
  nacionalidad: { id: number; nombre: string };
  nombre: string;
  numCud: string;
  numero: number;
  pathCud: string;
  pathCvProvisto: string;
  piso: string;
  representacion: string;
  roles: string[];
  sexo: string;
  telefono: string;
  telefonoAlternativo: string;
  usuarioGestor: string;
  idCiudad?: number;
  idNacionalidad?: number;
  idUsuarioGestor?: number;
}

type Props = { userData: UsuarioProps };

export const UpdateUser = (props: Props) => {
  const { userData } = props;
  const dispatch = useDispatch();
  const [localidades, setLocalidades] = useState<any[]>([]);
  const [alert, setAlert] = useState(false);
  const [values, setValues] = useState<UsuarioProps>({ ...userData });
  const [documento, setDocumento] = useState<any>();

  useEffect(() => {
    setValues({ ...userData });
  }, [userData]);

  const userLog = useSelector(userSelector);

  const pcias = useSelector(pciasSelector);
  useEffect(() => {
    dispatch(fetchProvincias());
  }, [dispatch]);

  const paises = useSelector(paisesSelector);
  useEffect(() => {
    dispatch(fetchPaises());
  }, [dispatch]);

  const sexos = useSelector(sexosSelector);
  const defaultGeneros = useSelector(generosSelector);

  const finalPaises = useMemo(() => {
    return Object.keys(paises).map((key, idx) => ({
      id: paises[key].idPais,
      title: paises[key].nombre,
    }));
  }, [paises]);

  const handleProvincias = useCallback((idPcia: string) => {
    axios.get(`${CVAPI}/ciudad?provincia=${idPcia}`).then((res) => {
      setLocalidades(res.data);
    });
  }, []);

  const handleSubmit = async () => {
    dispatch(init('Procesando el registro...'));

    const formData = new FormData();

    const copy = {
      ...values,
      idUsuario: values.idUsuario,
      roles: userLog.roles,
      idCiudad: values.ciudad?.id, // Agregar el campo idCiudad
      idNacionalidad: values.nacionalidad?.id, // Agregar el campo idNacionalidad
      idUsuarioGestor: values.usuarioGestor, // Agregar el campo idUsuarioGestor
    };

    const cleanedCopy = omit(copy, ['ciudad', 'nacionalidad', 'usuarioGestor']);

    if (documento && typeof documento !== 'string') formData.append('cud', documento);
    formData.append('userData', JSON.stringify(cleanedCopy));

    await axios.put(`${USERAPI}/usuarios`, formData);
    dispatch(finalize(null));
    setAlert(true);
  };

  const { ciudad } = userData || {};
  useEffect(() => {
    if (ciudad) {
      handleProvincias(ciudad.idProvincia.toString());
    }
  }, [ciudad, handleProvincias]);

  if (Object.keys(userData).length === 0)
    return <LoadingText message="Cargando información del usuario" />;

  const handleVerCUD = async (idUsuario: any) => {
    await axios
      .get(`${USERAPI}/usuarios/cud/${idUsuario}`, {
        responseType: 'blob',
      })
      .then((res: { data: BlobPart }) => {
        const file = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(file);

        window.open(url);
        return url;
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Define the validation schema here (replace schema with your actual validation rules).
  const schema = yup.object().shape({
    nombre: yup.string().required('El nombre es requerido'),
    apellido: yup.string().required('El apellido es requerido'),
    dni: yup
      .string()
      .required('El DNI es requerido')
      .min(7, 'El DNI debe tener al menos 7 dígitos')
      .max(8, 'El DNI debe tener como máximo 8 dígitos'),
    cuil: yup.string().required('El CUIL es requerido'),
    provincia: yup.string(),
    localidad: yup.string(),
    codPostal: yup
      .string()
      .required('El código postal es requerido')
      .max(4, 'El código postal debe tener 4 dígitos'),
    calle: yup.string().required('La calle es requerida').max(50, 'La calle debe tener 50 dígitos'),
    numero: yup
      .number()
      .required('El número es requerido')
      .max(99999, 'El número debe tener 5 dígitos máximo.'),
    piso: yup.string().max(3, 'El piso debe tener 3 dígitos máximo.'),
    departamento: yup.string(),
    email: yup
      .string()
      .required('El email es requerido')
      .email('El email debe ser válido')
      .max(150),
    emailAlternativo: yup
      .string()
      .required('El email alternativo es requerido')
      .email('El email alternativo debe ser válido')
      .max(150)
      .oneOf([yup.ref('email'), null], 'Ambos correos deben coincidir'),
    nacionalidad: yup
      .object()
      .shape({
        id: yup.number().required('La nacionalidad es requerida'),
        nombre: yup.string().required('La nacionalidad es requerida'),
      })
      .required('La nacionalidad es requerida'),
    fechaNacimiento: yup.string().required('La fecha de nacimiento es requerida'),
    sexo: yup.string(),
    genero: yup.string().required('El género es requerido'),
    nroTelefono: yup.string().max(20, 'El número de teléfono debe tener 20 dígitos máximo.'),
    nroTelefonoAlternativo: yup
      .string()
      .max(20, 'El número de teléfono debe tener 20 dígitos máximo.'),
    numCud: yup.string().nullable(),
  });

  return (
    <Formik
      validationSchema={schema}
      onSubmit={handleSubmit}
      initialValues={{
        ...userData,
      }}>
      {({ handleChange, values, errors, touched }) => (
        <Form>
          <FormSection>
            <Row className="mb-3">
              <Col xs={4}>
                <FormB.Label>Nombres</FormB.Label>
                <FormB.Control
                  id="nombre"
                  name="nombre"
                  value={values.nombre}
                  onChange={(e) => {
                    handleChange(e);
                    setValues({
                      ...values,
                      nombre: e.target.value,
                    });
                  }}
                />
                {touched.nombre && errors.nombre && (
                  <FormB.Text className="text-danger">{errors.nombre}</FormB.Text>
                )}
              </Col>
              <Col xs={4}>
                <FormB.Label>Apellidos</FormB.Label>
                <FormB.Control
                  id="apellido"
                  name="apellido"
                  value={values.apellido}
                  onChange={(e) => {
                    handleChange(e);
                    setValues({
                      ...values,
                      apellido: e.target.value,
                    });
                  }}
                />
                {touched.apellido && errors.apellido && (
                  <FormB.Text className="text-danger">{errors.apellido}</FormB.Text>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={4}>
                <FormB.Label>DNI</FormB.Label>
                <FormB.Control
                  id="dni"
                  name="dni"
                  title="D.N.I"
                  value={values.dni}
                  onChange={(e) => {
                    handleChange(e);
                    setValues({
                      ...values,
                      dni: e.target.value,
                    });
                  }}
                />
                {touched.dni && errors.dni && (
                  <FormB.Text className="text-danger">{errors.dni}</FormB.Text>
                )}
              </Col>
              <Col xs={4}>
                <FormB.Label>CUIT/CUIL</FormB.Label>
                <FormB.Control
                  id="cuil"
                  name="cuil"
                  title="CUIT/CUIL"
                  value={values.cuil}
                  onChange={(e) => {
                    handleChange(e);
                    setValues({
                      ...values,
                      cuil: e.target.value,
                    });
                  }}
                  readOnly
                />
              </Col>
            </Row>
          </FormSection>
          <Row className="mb-3">
            <Col xs={4}>
              <FormB.Label>Provincia</FormB.Label>
              <FormB.Control
                as="select"
                onChange={(e) => {
                  handleProvincias(e.target.value);
                  handleChange(e);
                  setValues({
                    ...values,
                    ciudad: {
                      ...values.ciudad,
                      idProvincia: Number(e.target.value),
                    },
                  });
                }}
                id="idProvincia"
                title="Provincia"
                name="ciudad.idProvincia"
                value={values.ciudad?.idProvincia | 0}>
                {pcias?.map((e: any) => (
                  <option key={e.idProvincia} value={e.idProvincia}>
                    {e.nombre}
                  </option>
                ))}
              </FormB.Control>
              {touched.ciudad?.idProvincia && errors.ciudad?.idProvincia && (
                <FormB.Text className="text-danger">{errors.ciudad.idProvincia}</FormB.Text>
              )}
            </Col>
            <Col xs={4}>
              <FormB.Label>Localidad</FormB.Label>
              <FormB.Control
                as="select"
                id="idCiudad"
                title="Localidad"
                name="ciudad.id"
                value={values.ciudad?.id | 0}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    ciudad: {
                      ...values.ciudad,
                      id: Number(e.target.value),
                      nombre: localidades
                        ? localidades.find((l) => l.idCiudad == Number(e.target.value))?.nombre
                        : '',
                    },
                  });
                }}>
                {localidades?.map(({ idCiudad, nombre }) => (
                  <option key={idCiudad} value={idCiudad}>
                    {nombre}
                  </option>
                ))}
              </FormB.Control>
              {touched.ciudad && errors.ciudad && (
                <FormB.Text className="text-danger">{errors.ciudad}</FormB.Text>
              )}
            </Col>
            <Col xs={4}>
              <FormB.Label>Código Postal</FormB.Label>
              <FormB.Control
                id="codPostal"
                title="Código Postal"
                value={values.codPostal}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    codPostal: e.target.value,
                  });
                }}
              />
              {touched.codPostal && errors.codPostal && (
                <FormB.Text className="text-danger">{errors.codPostal}</FormB.Text>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={4}>
              <FormB.Label>Calle</FormB.Label>
              <FormB.Control
                id="calle"
                title="Calle"
                name="calle"
                value={values.calle}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    calle: e.target.value,
                  });
                }}
              />
              {touched.calle && errors.calle && (
                <FormB.Text className="text-danger">{errors.calle}</FormB.Text>
              )}
            </Col>
            <Col xs={2}>
              <FormB.Label>Número</FormB.Label>
              <FormB.Control
                id="numero"
                title="Número"
                name="numero"
                value={values.numero}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    numero: Number(e.target.value),
                  });
                }}
              />
              {touched.numero && errors.numero && (
                <FormB.Text className="text-danger">{errors.numero}</FormB.Text>
              )}
            </Col>
            <Col xs={2}>
              <FormB.Label>Piso</FormB.Label>
              <FormB.Control
                id="piso"
                name="piso"
                title="Piso"
                value={values.piso}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    piso: e.target.value,
                  });
                }}
              />
            </Col>
            <Col xs={2}>
              <FormB.Label>Departamento</FormB.Label>
              <FormB.Control
                id="departamento"
                name="departamento"
                title="Departamento"
                value={values.departamento}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    departamento: e.target.value,
                  });
                }}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={6}>
              <FormB.Label>E-mail</FormB.Label>
              <FormB.Control
                id="email"
                title="E-mail"
                name="email"
                value={values.email}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    email: e.target.value,
                  });
                }}
              />
              {touched.email && errors.email && (
                <FormB.Text className="text-danger">{errors.email}</FormB.Text>
              )}
            </Col>
            <Col xs={6}>
              <FormB.Label>Ingresa tu e-mail nuevamente</FormB.Label>
              <FormB.Control
                id="emailAlternativo"
                name="emailAlternativo"
                title="Ingresa tu e-mail nuevamente*"
                value={values.emailAlternativo}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    emailAlternativo: e.target.value,
                  });
                }}
              />
              {touched.emailAlternativo && errors.emailAlternativo && (
                <FormB.Text className="text-danger">{errors.emailAlternativo}</FormB.Text>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={6}>
              <FormB.Label>Nacionalidad</FormB.Label>
              <FormB.Control
                as="select"
                id="idNacionalidad"
                name="nacionalidad.id"
                title="Nacionalidad"
                value={values.nacionalidad?.id || 0}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedNacionalidad = finalPaises.find((p) => p.id === Number(selectedId));

                  if (selectedNacionalidad) {
                    handleChange(e);
                    setValues({
                      ...values,
                      nacionalidad: {
                        id: selectedNacionalidad.id,
                        nombre: selectedNacionalidad.title,
                      },
                    });
                  }
                }}>
                {paises &&
                  finalPaises.map((pais: any) => (
                    <option key={pais.id} value={pais.id}>
                      {pais.title}
                    </option>
                  ))}
              </FormB.Control>
            </Col>

            <Col xs={6}>
              <FormB.Label>Fecha de Nacimiento</FormB.Label>
              <FormB.Control
                type="date"
                id="fechaNacimiento"
                name="fechaNacimiento"
                title="Fecha de Nacimiento"
                value={values.fechaNacimiento}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    fechaNacimiento: e.target.value,
                  });
                }}
              />
              {touched.fechaNacimiento && errors.fechaNacimiento && (
                <FormB.Text className="text-danger">{errors.fechaNacimiento}</FormB.Text>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={4}>
              <FormB.Label>Sexo</FormB.Label>
              <FormB.Control
                as="select"
                id="sexo"
                title="Sexo"
                name="sexo"
                placeholder="Como aparece en tu DNI"
                value={values.sexo}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    sexo: e.target.value,
                  });
                }}>
                {sexos?.map((e: any) => (
                  <option key={e.key} value={e.key}>
                    {e.desc}
                  </option>
                ))}
              </FormB.Control>
            </Col>
            <Col xs={4}>
              <FormB.Label>Género</FormB.Label>
              <FormB.Control
                as="select"
                id="genero"
                name="genero"
                title="Género"
                value={values.genero}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    genero: e.target.value,
                  });
                }}>
                {defaultGeneros &&
                  [...defaultGeneros, 'Ninguno de los anteriores'].map((e: string) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
              </FormB.Control>
            </Col>
            <Col
              xs={4}
              style={{
                display: values.genero !== 'Ninguno de los anteriores' ? 'none' : 'visible',
              }}>
              <FormB.Label>Cual?</FormB.Label>
              <FormB.Control
                id="genero_otro"
                name="genero_otro"
                title="Cual?"
                value={defaultGeneros.includes(values.genero) ? '' : values.genero}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    genero: e.target.value,
                  });
                }}
              />
            </Col>
            {touched.genero && errors.genero && (
              <FormB.Text className="text-danger">{errors.genero}</FormB.Text>
            )}
          </Row>
          <Row className="mb-3">
            <Col xs={6}>
              <FormB.Label>Número de celular/Teléfono personal</FormB.Label>
              <FormB.Control
                id="telefono"
                title="Número de celular/Teléfono personal"
                name="telefono"
                placeholder="Incluya el código de área. Ejemplo: 011 1234 5678"
                value={values.telefono}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    telefono: e.target.value,
                  });
                }}
              />
            </Col>
            <Col xs={6}>
              <FormB.Label>Número de celular/Teléfono alternativo</FormB.Label>
              <FormB.Control
                id="telefonoAlternativo"
                title="Número de celular/Teléfono alternativo"
                name="telefonoAlternativo"
                placeholder="Incluya el código de área. Ejemplo: 011 1234 5678"
                value={values.telefonoAlternativo}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    telefonoAlternativo: e.target.value,
                  });
                }}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col xs={6}>
              <FormB.Label>Número de Certificado Único de Discapacidad (CUD).</FormB.Label>
              <FormB.Control
                id="numCud"
                name="numCud"
                title="Número de Certificado Único de Discapacidad (CUD)."
                value={values.numCud}
                onChange={(e) => {
                  handleChange(e);
                  setValues({
                    ...values,
                    numCud: e.target.value,
                  });
                }}
              />
            </Col>
            <Col xs={6}>
              <FormB.Group>
                <FormB.Label>
                  <span>
                    {`Cargar Certificado Único de Discapacidad CUD`}
                    <i className="fa fa-wheelchair" />
                  </span>
                </FormB.Label>
                <FormB.File custom id="cud">
                  <FormB.File.Input
                    id="cud"
                    name="cud"
                    accept=".pdf,.doc,.odt,.jpg,.png"
                    type="file"
                    onChange={(e: any) => setDocumento(e.currentTarget.files[0])}
                  />
                  <FormB.File.Label data-browse="Cargar archivo">
                    {documento ? documento.name : values.pathCud}
                  </FormB.File.Label>
                </FormB.File>

                {values.pathCud && (
                  <LinkBtn
                    onClick={() => handleVerCUD(userData.idUsuario)}
                    style={{ whiteSpace: 'nowrap', display: 'flex', justifyContent: 'flex-end' }}>
                    Ver Cud
                  </LinkBtn>
                )}
              </FormB.Group>
            </Col>
          </Row>
          <small className="help-block form-text" style={{ fontSize: 12 }}>
            Deberá especificar el número del Certificado Único de Discapacidad o demás Certificados
            de Discapacidad vigentes, expedidos por autoridades competentes según lo dispuesto en el
            Artículo 3º de la Ley Nº 22.431. - El formato de archivo soportado es .PDF
          </small>
          <Row className="d-flex justify-content-end">
            <Button type="submit" variant="success" style={{ marginLeft: '10pt' }}>
              Guardar
            </Button>
          </Row>
          <AlertaDialog size="sm" show={alert} onClose={() => setAlert(false)}>
            <div className="text-center">
              <i
                className="text-success fa fa-check-circle-o"
                style={{ fontSize: 64, WebkitTextStroke: '5px white' }}></i>
              <p>
                <span style={{ fontWeight: 'bold', fontSize: 24 }}>Actualizacion Exitosa!</span>
                <br />
                Ha actualizado su perfil exitosamente.
              </p>
            </div>
          </AlertaDialog>
        </Form>
      )}
    </Formik>
  );
};

export default UpdateUser;
